import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
  type CSSProperties,
} from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import type { EmblaEventType } from "embla-carousel";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { useRouter } from "@tanstack/react-router";
import { cn, generateTextShadow } from "@/lib/utils";
import { DefaultButton } from "@/components/DefaultButton";
import {
  useClaimGacha,
  queryClient,
  useStats,
  useGachaPrize,
  useItemDetails,
} from "@/lib/hooks/useApi";
import { usePrivy } from "@privy-io/react-auth";
import { toast } from "sonner";
import { useBalance } from "@/lib/hooks/useBalance";
import type { GachaClaimItem, GachaPrizeItem } from "@/lib/types/api";
import ResourceBar from "@/components/ResourceBar";
import { mockStatsDataResponse } from "@/mocks/statsData";
import { ItemFreeGachaDialog } from "@/components/ItemFreeGachaDialog";
import FreeGachaDialogButton from "@/components/FreeGachaDialogButton";
import { t } from "@/lib/i18n";
import { PrizeSummaryDialog } from "./-components/PrizeSummaryDialog";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { ApngOnce } from "@/components/ApngOnce";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ScrollArea } from "@/components/ui/scroll-area";
import { ItemIcon } from "@/routes/(game)/shop/-components/ItemIcon";
import {
  translateInventoryItemName,
  translateInventoryItemSubtitle,
  translateRarityName,
} from "@/lib/inventoryTranslations";
import { useMusic } from "@/lib/hooks/useMusic";
import { MODEL_REGISTRY, type ModelType } from "@/components/RocketsOverlay";
import { Canvas } from "@react-three/fiber";
import { OrthographicCamera } from "@react-three/drei";
import * as THREE from "three";

const TWEEN_FACTOR_BASE = 0.52;
// const GACHA_VIDEO_SRC = "https://cdn.navalabs.io/welcome-pack_bLx2T3.mp4";
const GACHA_VIDEO_SRC =
  "https://cdn.navalabs.io/components/gacha/gacha-transition_89Na22.mp4";
// const REVEAL_ORB_SRC =
//   "https://cdn.navalabs.io/components/gacha/gacha-ball-reveal_38G3Es.png";
// const REVEAL_ORB_FALLBACK_SRC =
//   "https://cdn.navalabs.io/components/gacha/gacha-ball-reveal_38G3Es.png";
const REVEAL_ORB_SRC = "/assets/gacha/gacha-ball-reveal_48G3Es.png";
const REVEAL_ORB_FALLBACK_SRC = "/assets/gacha/gacha-ball-reveal_48G3Es.png";
const TRANSITION_OPENED_SRC =
  "https://cdn.navalabs.io/components/gacha/gacha-transition-opened_HxhYC8.png";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TRANSITION_OPENED_FALLBACK_SRC =
  "https://cdn.navalabs.io/components/gacha/gacha-transition-opened_HxhYC8.png";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const POINTER_SEEN_STORAGE_KEY = "cfl:gachaPointerSeen";

const GACHA_TITLE = [
  "https://cdn.navalabs.io/components/gacha/title-common_rHv6s4.png",
  "https://cdn.navalabs.io/components/gacha/title-epic_h6EDr6.png",
  "https://cdn.navalabs.io/components/gacha/title-legendary_Zg7Kot.png",
];

const MAX_QUANTITY = 10;
const SPARKLE_BURSTS = [
  {
    x: -180,
    y: -80,
    size: 36,
    delay: 0.05,
    shape: "diamond",
  },
  {
    x: -100,
    y: -180,
    size: 24,
    delay: 0.1,
    shape: "dot",
  },
  { x: 20, y: -220, size: 30, delay: 0.12, color: "#B8FFB0", shape: "dot" },
  {
    x: 120,
    y: -160,
    size: 24,
    delay: 0.08,
    shape: "diamond",
  },
  {
    x: 190,
    y: -40,
    size: 24,
    delay: 0.14,
    shape: "dot",
  },
  {
    x: 140,
    y: 100,
    size: 36,
    delay: 0.18,
    shape: "diamond",
  },
  {
    x: 0,
    y: 180,
    size: 30,
    delay: 0.16,
    shape: "dot",
  },
  {
    x: -140,
    y: 120,
    size: 24,
    delay: 0.2,
    shape: "dot",
  },
  {
    x: -220,
    y: -10,
    size: 18,
    delay: 0.06,
    shape: "diamond",
  },
  {
    x: -40,
    y: -200,
    size: 16,
    delay: 0.09,
    shape: "dot",
  },
  {
    x: 60,
    y: -200,
    size: 14,
    delay: 0.11,
    shape: "diamond",
  },
  {
    x: 220,
    y: 10,
    size: 16,
    delay: 0.13,
    shape: "dot",
  },
  {
    x: 60,
    y: 170,
    size: 18,
    delay: 0.17,
    shape: "dot",
  },
  {
    x: -200,
    y: 40,
    size: 14,
    delay: 0.19,
    shape: "diamond",
  },
] as const;

const numberWithinRange = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const Route = createFileRoute("/(game)/gacha/")({
  component: RouteComponent,
});

const MOCK_REVEALED_ITEMS = [
  {
    misc: null,
    itemId: "73d7e815-af2c-4519-b352-ce636d615e69",
    rarity: "common",
    imageUrl: null,
    itemData: null,
    itemType: "chart-frame",
    itemName: "UFO Frame",
    refunded: false,
    sequenceId: "0e35971d-8077-4330-b387-0cd35719535d",
    description:
      "Rhythmic pulse - gentle heartbeat! (Note: In-chart frame appears on line chart during matches)",
    inventoryId: "c44e5c26-2615-43c9-80a6-0beb67dc175f",
    alreadyOwned: false,
    refundAmount: 0,
    sequenceNumber: 10,
    gachaMetadataId: "c79407c5-b409-46c3-bca9-b86a4d62f5a8",
    videoPreviewUrl: null,

    // misc: null,
    // itemId: "eda7a49f-24c2-4b92-9b5a-aab6498684db",
    // rarity: "rare",
    // imageUrl: "https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png",
    // itemData: null,
    // itemName: "Gacha Coin",
    // itemType: "gacha",
    // refunded: true,
    // sequenceId: "68dea9d4-dad5-4db0-8a80-4d3edb5c7129",
    // description: null,
    // inventoryId: "e2b26ca9-e73c-4db7-bc9f-c2c1d6149735",
    // alreadyOwned: true,
    // refundAmount: 5,
    // partnerXHandle: "https://x.com/cfldotfun",
    // sequenceNumber: 1,
    // gachaMetadataId: "ec3b5808-0bdb-4333-a2a3-0ef7f5068352",
    // videoPreviewUrl: null,

    // misc: null,
    // itemId: "d4c02407-b902-46ad-9ad7-8499fd1b9ef6",
    // rarity: "common",
    // imageUrl: "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/cfl-asset/in-chart-frame/glitch-green-in-chart-frame.png",
    // itemData: null,
    // itemName: "Glitch Green",
    // itemType: "chart-frame",
    // refunded: false,
    // sequenceId: "aade6271-a93b-4a2a-bd4a-88fdd06e736b",
    // description: "Digital chaos - pixel disruption fortifying!",
    // inventoryId: "06578347-1e2c-4951-832e-c96cbc4f93eb",
    // alreadyOwned: false,
    // refundAmount: 0,
    // partnerXHandle: "https://x.com/cfldotfun",
    // sequenceNumber: 4,
    // gachaMetadataId: "4fd59946-ba1e-4d10-87ef-9c7fc3662742",
    // videoPreviewUrl: null

    // misc: "8a12ca44-e850-425d-bd3c-cb7921a78051",
    // itemId: "b6740763-6a4b-4254-9848-2254eddb5df7",
    // rarity: "common",
    // imageUrl:
    //   "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/gacha_asset/cherry.png?t=1",
    // itemData: null,
    // itemName: "0.02 USD by cherry.fun",
    // itemType: "referral-code",
    // refunded: false,
    // sequenceId: "1c4c4ea9-cdf1-4c06-aee5-650705b9cf74",
    // description:
    //   "<strong>How to Claim:</strong><br><br>\n1. Download Cherry from Seeker dApp Store (or visit chat.cherry.fun) and connect your wallet.<br>\n2. From the main menu, open Global Degen Club.<br>\n3. Tap New Message or (+) icon & search for <strong>cherrydev.sol</strong>.<br>\n4. Send a DM & include your claim code in the message.<br><br>\nThe reward will be sent to the same wallet that sends the DM.",
    // inventoryId: "239b9f7c-43cc-4ac9-b7a2-fd6dc567e531",
    // alreadyOwned: false,
    // refundAmount: 0,
    // partnerXHandle: "https://x.com/cherrydotfun",
    // sequenceNumber: 15,
    // gachaMetadataId: "a399f66e-57d7-4c00-af82-cf7dbfeb30ef",
    // videoPreviewUrl: null,

    //   misc: null,
    //   itemId: "ae2d8ca3-7e46-414a-9bfd-ad083532a185",
    //   rarity: "common",
    //   imageUrl: null,
    //   itemData: {
    //     cosmeticName: "Cyan Dotted Line",
    //     cosmeticChartBaseType: "dotted",
    //     cosmeticChartBaseColor: "hsl(187.94,86%,53%)"
    //   },
    //   itemName:"Cyan Dotted Line",
    //   itemType: "chart-line",
    //   refunded: false,
    //   sequenceId: "8a23422f-828a-4186-a696-a2c83859029b",
    //   description: "Rhythmic pulse - binary rhythm!",
    //   inventoryId: "4e368021-3255-4aaa-b825-aa0ee17ef411",
    //   alreadyOwned: false,
    //   refundAmount: 0,
    //   sequenceNumber: 12,
    //   gachaMetadataId: "191d641d-a5d3-4f93-b089-3594d4b9af96",
    //   videoPreviewUrl: null
  },
];

function RouteComponent() {
  const USE_MOCK_STATS = false; //NOTE: toggle to false if the API is ready
  const USE_MOCK_REVEALED_ITEMS = false; //NOTE: Toggle to test revealed items with mock data
  const { user } = usePrivy();
  const navigate = useNavigate();
  const router = useRouter();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [videoInstance, setVideoInstance] = useState(0);
  const [isTransitionOpen, setIsTransitionOpen] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState<"apng" | "ready">(
    "apng",
  );
  const [revealedItems, setRevealedItems] = useState<GachaClaimItem[]>(
    USE_MOCK_REVEALED_ITEMS
      ? (MOCK_REVEALED_ITEMS as unknown as GachaClaimItem[])
      : [],
  );
  const [activeRevealIndex, setActiveRevealIndex] = useState(0);
  const [isRevealOpen, setIsRevealOpen] = useState(false);
  const [revealPhase, setRevealPhase] = useState<"apng" | "content">("apng");
  const [isMachineDialogOpen, setIsMachineDialogOpen] = useState(false);
  const [isPrizeSummaryOpen, setIsPrizeSummaryOpen] = useState(false);
  const [isConfirmSpinOpen, setIsConfirmSpinOpen] = useState(false);
  const [hasSeenPointer, setHasSeenPointer] = useState(false);
  const {
    mute: muteMusic,
    unmute: unmuteMusic,
    isMuted: isMusicMuted,
  } = useMusic();
  const tweenFactorRef = useRef(0);
  const tweenNodesRef = useRef<HTMLElement[]>([]);
  const hasScrolledToCenterRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const skipRevealAnimationRef = useRef(false);
  const musicWasMutedBeforeVideoRef = useRef<boolean | null>(null);
  const storedMutePreferenceRef = useRef<boolean | null>(null);
  const hasActiveVideoMuteRef = useRef(false);
  const [isPending, startTransition] = useTransition();

  type GachaTierKey = "frog" | "racoon" | "dragon";

  const tierOrder: GachaTierKey[] = ["frog", "racoon", "dragon"];
  const tierImages: Record<GachaTierKey, string> = {
    frog: "https://cdn.navalabs.io/components/gacha/frog-idle_8GGDah.png",
    racoon: "https://cdn.navalabs.io/components/gacha/racoon-idle_5zFsRB.png",
    dragon: "https://cdn.navalabs.io/components/gacha/dragon-idle_RTpR23.png",
  };
  const walletAddress = user?.wallet?.address;
  const { mutateAsync: claimGacha } = useClaimGacha();
  const { data: gachaPrizeData, isLoading: isGachaPrizeLoading } =
    useGachaPrize();

  const apiResponse = useStats(walletAddress ?? "", false);

  const { data: statsData, refetch: refetchStats } = USE_MOCK_STATS
    ? {
        data: mockStatsDataResponse,
        refetch: async () => ({ data: mockStatsDataResponse }),
      }
    : apiResponse;

  const gameWalletAddress = statsData?.embeddedWallet;
  const { refetch: refetchBalance } = useBalance(gameWalletAddress ?? "");

  const currentTier = (activeIndex % 3) + 1;

  // Price per tier in gacha coins
  const tierPrices: Record<number, number> = {
    1: 1,
    2: 5,
    3: 10,
  };

  const price = tierPrices[currentTier];
  const totalPrice = price * quantity;

  // Reset quantity when tier changes
  useEffect(() => {
    setQuantity(1);
  }, [currentTier]);

  useEffect(() => {
    if (!isTransitionOpen) {
      return;
    }
    setTransitionPhase("apng");
  }, [isTransitionOpen]);

  useEffect(() => {
    if (!isRevealOpen) {
      return;
    }
    if (skipRevealAnimationRef.current) {
      skipRevealAnimationRef.current = false;
      setRevealPhase("content");
      return;
    }
    setRevealPhase("apng");
  }, [activeRevealIndex, isRevealOpen]);

  const restoreMusicIfNeeded = useCallback(() => {
    if (!hasActiveVideoMuteRef.current) {
      return;
    }

    const storedPreference = storedMutePreferenceRef.current;
    const wasMutedBefore = musicWasMutedBeforeVideoRef.current;

    hasActiveVideoMuteRef.current = false;
    storedMutePreferenceRef.current = null;
    musicWasMutedBeforeVideoRef.current = null;

    const shouldRemainMuted =
      storedPreference !== null ? storedPreference : (wasMutedBefore ?? false);

    if (!shouldRemainMuted) {
      unmuteMusic();
    }
  }, [unmuteMusic]);

  useEffect(() => {
    return () => {
      restoreMusicIfNeeded();
    };
  }, [restoreMusicIfNeeded]);

  // useEffect(() => {
  //   const seen = sessionStorage.getItem(POINTER_SEEN_STORAGE_KEY);
  //   setHasSeenPointer(seen === "true");
  // }, []);

  const markPointerSeen = useCallback(() => {
    setHasSeenPointer(true);
    // sessionStorage.setItem(POINTER_SEEN_STORAGE_KEY, "true");
  }, []);

  const handleStartVideo = useCallback(() => {
    if (!hasActiveVideoMuteRef.current) {
      musicWasMutedBeforeVideoRef.current = isMusicMuted;
      storedMutePreferenceRef.current = (() => {
        if (typeof window === "undefined") {
          return null;
        }
        try {
          const value = window.localStorage.getItem("cfl:music-muted");
          return value === null ? null : value === "true";
        } catch {
          return null;
        }
      })();

      if (!isMusicMuted) {
        hasActiveVideoMuteRef.current = true;
        muteMusic();
      }
    }

    setVideoInstance((value) => value + 1);
    setIsVideoOpen(true);
    requestAnimationFrame(() => {
      const element = videoRef.current;
      if (!element) return;
      element.currentTime = 0;
      // const userAgent =
      //   typeof navigator !== "undefined" ? navigator.userAgent : "";
      // const isFirefox = /firefox/i.test(userAgent);
      // const isMozillaEngine =
      //   /gecko/i.test(userAgent) && !/like gecko/i.test(userAgent);
      // if (!isFirefox && !isMozillaEngine) {
      //   element.muted = false;
      // }
      element.muted = false;
      void element.play().catch(() => {
        // If autoplay fails, keep the video visible for manual start.
      });
    });
  }, [isMusicMuted, muteMusic, setIsVideoOpen, setVideoInstance, videoRef]);

  const handleGachaClaim = () => {
    startTransition(async () => {
      try {
        setIsTransitionOpen(false);
        setIsRevealOpen(false);
        setRevealedItems([]);
        setActiveRevealIndex(0);

        const claimResponse = await claimGacha({
          gachaType: currentTier - 1,
          count: quantity,
        });
        const sortedItems = [...(claimResponse?.items ?? [])].sort(
          (a, b) => a.sequenceNumber - b.sequenceNumber,
        );
        setRevealedItems(sortedItems);
        setActiveRevealIndex(0);

        await queryClient.invalidateQueries({ queryKey: ["inventory"] });
        await refetchBalance();
        await refetchStats();

        handleStartVideo();
      } catch (error) {
        console.error("Error claiming gacha:", error);

        let message = "An unexpected error occurred";
        if (
          typeof error === "object" &&
          error !== null &&
          "response" in error
        ) {
          const err = error as { response?: { data?: { error?: string } } };
          message = err.response?.data?.error ?? message;
        } else if (error instanceof Error) {
          message = error.message;
        }

        toast.error("Claim Failed", {
          description: message,
          position: "top-center",
        });
      }
    });
  };

  const handleRevealAdvance = async () => {
    if (activeRevealIndex + 1 < revealedItems.length) {
      setActiveRevealIndex((prev) => prev + 1);
      await refetchStats();
      return;
    }
    setIsRevealOpen(false);
  };

  const handleShareToTwitter = () => {
    if (!activeRevealItem) return;

    const itemName = translateInventoryItemName(
      activeRevealItem.itemName,
      undefined,
      activeRevealItem.itemId,
    );

    // Get partner name from item details or fall back to empty string
    const detailedItem = itemDetails?.find(
      (detail) => detail.id === activeRevealItem.itemId,
    );
    const partnerHandle = detailedItem?.partnerXHandle || "";

    // Format partner handle: extract from URL if needed and format as @handle
    let formattedPartnerHandle = "";
    if (partnerHandle) {
      // Remove https:// or http:// if present
      let handle = partnerHandle.replace(/^https?:\/\//, "");
      // Remove x.com/ or twitter.com/ if present
      handle = handle.replace(/^(x\.com|twitter\.com)\//, "");
      // Format as @handle
      formattedPartnerHandle = handle ? `@${handle}` : "";
    }

    // Get referral code and construct share URL
    const refCode = statsData?.referralCode;
    const shareUrl = `${window.location.origin}/?ref=${refCode}`;

    // Build tweet text
    const partnerText = formattedPartnerHandle
      ? ` by ${formattedPartnerHandle}`
      : "";

    const cflUrl = "https://x.com/cfldotfun";
    let cflHandle = cflUrl.replace(/^https?:\/\//, "");
    cflHandle = cflHandle.replace(/^(x\.com|twitter\.com)\//, "");
    const formattedCflHandle = cflHandle ? `@${cflHandle}` : "CFL";

    const tweetText = `I just pulled ${itemName}${partnerText} on ${formattedCflHandle} Gacha.\nJoin via my link to win prizes from the $40K prize pool: ${shareUrl}`;

    const twitterIntentUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;

    window.open(twitterIntentUrl, "_blank", "noopener,noreferrer");
  };

  // const handleTransitionAnimationEnd = useCallback(() => {
  //   const timeoutId = setTimeout(() => {
  //     setTransitionPhase("ready");
  //   }, 1000);
  //   return () => {
  //     clearTimeout(timeoutId);
  //   };
  // }, [setTransitionPhase]);

  const handleRevealAnimationEnd = useCallback(() => {
    setRevealPhase("content");
  }, [setRevealPhase]);

  const handleSkipToReveal = useCallback(() => {
    const element = videoRef.current;
    if (element) {
      element.pause();
      element.currentTime = 0;
    }

    setIsVideoOpen(false);
    restoreMusicIfNeeded();
    setIsTransitionOpen(false);

    if (revealedItems.length > 0) {
      skipRevealAnimationRef.current = true;
      setIsRevealOpen(true);
      handleRevealAnimationEnd();
    }
  }, [handleRevealAnimationEnd, restoreMusicIfNeeded, revealedItems.length]);

  // Auto-open reveal dialog when using mock data for testing
  useEffect(() => {
    if (USE_MOCK_REVEALED_ITEMS && revealedItems.length > 0 && !isRevealOpen) {
      setIsRevealOpen(true);
      setRevealPhase("apng");
    }
  }, [USE_MOCK_REVEALED_ITEMS, revealedItems.length, isRevealOpen]);

  const setTweenNodes = useCallback((api: NonNullable<CarouselApi>) => {
    tweenNodesRef.current = api.slideNodes().map((slideNode) => {
      const target =
        slideNode.querySelector<HTMLElement>("[data-gacha-machine]") ??
        (slideNode as HTMLElement);
      return target;
    });
  }, []);

  const setTweenFactor = useCallback((api: NonNullable<CarouselApi>) => {
    tweenFactorRef.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback(
    (api: NonNullable<CarouselApi>, eventName?: EmblaEventType) => {
      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === "scroll";

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) {
            return;
          }

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }

                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue =
            1 - Math.abs(diffToTarget * tweenFactorRef.current);
          const scale = numberWithinRange(tweenValue, 0.8, 1).toString();
          const tweenNode = tweenNodesRef.current[slideIndex];

          if (tweenNode) {
            tweenNode.style.transform = `scale(${scale})`;
          }
        });
      });
    },
    [],
  );

  const tweenScaleOnScroll = useCallback(
    (api: NonNullable<CarouselApi>) => tweenScale(api, "scroll"),
    [tweenScale],
  );

  useEffect(() => {
    const api = carouselApi;
    if (!api) {
      return;
    }

    setTweenNodes(api);
    setTweenFactor(api);
    tweenScale(api);
    setActiveIndex(api.selectedScrollSnap());

    // Scroll to center item (index 3) on initial render to show Tier 1 with side items visible
    if (!hasScrolledToCenterRef.current) {
      hasScrolledToCenterRef.current = true;
      // Wait for carousel to fully initialize
      const scrollToCenter = () => {
        if (api.scrollSnapList().length > 0) {
          // Scroll to index 3 (Tier 1 in the middle set)
          api.scrollTo(3, false);
          // Re-apply tween after scroll
          setTimeout(() => {
            setTweenNodes(api);
            setTweenFactor(api);
            tweenScale(api);
          }, 100);
        }
      };
      // Wait for carousel to be ready
      setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollToCenter);
        });
      }, 100);
    }

    api
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", tweenScale)
      .on("scroll", tweenScaleOnScroll)
      .on("slideFocus", tweenScale)
      .on("select", () => setActiveIndex(api.selectedScrollSnap()));

    return () => {
      api.off("reInit", setTweenNodes);
      api.off("reInit", setTweenFactor);
      api.off("reInit", tweenScale);
      api.off("scroll", tweenScaleOnScroll);
      api.off("slideFocus", tweenScale);
    };
  }, [
    carouselApi,
    setTweenNodes,
    setTweenFactor,
    tweenScale,
    tweenScaleOnScroll,
  ]);

  const activeRevealItem = revealedItems[activeRevealIndex] ?? revealedItems[0];
  const isTransitionReady = transitionPhase === "ready";
  const isRevealReady = revealPhase === "content";

  // Get item details for sharing (includes partnerXHandle)
  const { data: itemDetails } = useItemDetails(
    statsData?.userId,
    activeRevealItem?.itemId ?? "",
  );
  const gachaPrizeItems = useMemo(() => {
    if (!gachaPrizeData) return [];
    if (currentTier === 2)
      return [...gachaPrizeData.tier1, ...gachaPrizeData.tier2];
    if (currentTier === 3)
      return [
        ...gachaPrizeData.tier1,
        ...gachaPrizeData.tier2,
        ...gachaPrizeData.tier3,
      ];
    return gachaPrizeData.tier1 ?? [];
  }, [gachaPrizeData, currentTier]);
  const gachaPrizeRarityOrder = [
    "legendary",
    "epic",
    "rare",
    "uncommon",
    "common",
  ];
  const gachaPrizeItemsByRarity = useMemo(() => {
    return gachaPrizeItems.reduce<Record<string, GachaPrizeItem[]>>(
      (acc, item) => {
        const key = item.rarity?.toLowerCase() || "common";
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {},
    );
  }, [gachaPrizeItems]);

  const renderGachaPrizeGrid = (items: GachaPrizeItem[]) => (
    <div className="grid grid-cols-3 gap-3 w-full">
      {items.map((item) => (
        <div
          key={item.id}
          className="dark-blue-container relative flex flex-col items-center w-full aspect-square overflow-visible py-2"
        >
          <div className="flex-1 flex items-center justify-center pointer-events-none">
            {Object.keys(MODEL_REGISTRY).includes(item.id) ? (
              (() => {
                const ModelComponent = MODEL_REGISTRY[item.id as ModelType];
                return (
                  <div className="w-full h-12 overflow-auto object-cover pointer-events-none">
                    <Canvas style={{ pointerEvents: "none" }}>
                      <OrthographicCamera
                        makeDefault
                        position={[0, 0, 1000]}
                        zoom={1}
                      />
                      <ambientLight position={[0, 0, 10]} intensity={3} />
                      <directionalLight
                        position={[0, THREE.MathUtils.degToRad(40), 0]}
                        intensity={12}
                      />
                      <ModelComponent rotation={0} />
                    </Canvas>
                  </div>
                );
              })()
            ) : (
              <ItemIcon
                item={item}
                className={cn(
                  item.name === "Trophy" ? "w-10" : "w-12",
                  "h-auto object-cover pointer-events-none",
                )}
                userProfilePicture={statsData?.profilePicture}
              />
            )}
          </div>
          <div className="w-full flex flex-col items-center justify-center gap-0.5 px-2 z-10">
            <span
              className="text-white text-xs font-black uppercase text-center line-clamp-2 wrap-break-word"
              style={{ textShadow: generateTextShadow(1.25) }}
            >
              {translateInventoryItemName(item.name, undefined, item.id)}
            </span>
            <span className="text-white/70 text-[10px] text-center leading-tight line-clamp-1 wrap-break-word">
              {translateInventoryItemSubtitle(
                item.subtitle ?? "",
                undefined,
                item.id,
              )}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <style>{`
        @keyframes gacha-pop {
          0% {
            transform: scale(0.2) rotate(-12deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.18) rotate(6deg);
            opacity: 1;
          }
          80% {
            transform: scale(0.95) rotate(-2deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        @keyframes gacha-float-in {
          0% {
            transform: translateY(12px) scale(0.98);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes gacha-bob {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        @keyframes gacha-sparkle {
          0% {
            transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
            opacity: 0;
          }
          35% {
            opacity: 1;
          }
          100% {
            transform: translate(
                calc(-50% + var(--x)),
                calc(-50% + var(--y))
              )
              scale(1.2)
              rotate(30deg);
            opacity: 0;
          }
        }

        @keyframes gacha-loading-bar {
          0% {
            transform: translateX(-120%);
          }
          100% {
            transform: translateX(220%);
          }
        }
      `}</style>
      <video
        src={GACHA_VIDEO_SRC}
        preload="auto"
        playsInline
        className="hidden"
        aria-hidden="true"
      />
      <Dialog open={isMachineDialogOpen} onOpenChange={setIsMachineDialogOpen}>
        <DialogContent
          aria-label="Gacha machine dialog"
          className="relative max-w-[24rem]! p-4"
        >
          <div className="flex flex-col gap-4 h-full max-h-[80vh] overflow-scroll">
            <span
              className="text-white font-black text-xl text-center uppercase"
              style={{ textShadow: generateTextShadow(1.4) }}
            >
              {t("inventory.gacha")} - TIER {currentTier}
            </span>
            <div className="p-1 flex flex-col gap-6">
              {isGachaPrizeLoading ? (
                <div className="flex items-center justify-center py-6">
                  <img
                    src="/assets/loading-pixel.png"
                    className="w-1/8"
                    style={{
                      imageRendering: "pixelated",
                    }}
                  />
                </div>
              ) : (
                gachaPrizeRarityOrder.map((rarity) => {
                  const rarityItems = gachaPrizeItemsByRarity[rarity] || [];
                  if (rarityItems.length === 0) return null;

                  return (
                    <div key={rarity} className="flex flex-col gap-2 w-full">
                      <div className="w-full px-2">
                        <span
                          className="text-white font-black text-lg"
                          style={{ textShadow: generateTextShadow(1.2) }}
                        >
                          {translateRarityName(rarity)}
                        </span>
                      </div>
                      {renderGachaPrizeGrid(rarityItems)}
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <DialogClose asChild>
            <DefaultButton className="absolute bottom-0 inset-x-0 z-40">
              <span>CLOSE</span>
            </DefaultButton>
          </DialogClose>
        </DialogContent>
      </Dialog>
      <main className="relative shrink-0! flex-1 flex flex-col items-center justify-center bg-[#1D2B53]">
        <div className="absolute flex items-start ps-16 pe-2 top-4 left-0 z-40 w-full">
          <ResourceBar className="flex w-full" showGachaCoin />
        </div>

        <div className="flex flex-col items-center justify-center gap-1 absolute top-16 right-2 z-50">
          {statsData?.gachaCoinEligibility.isEligibleForGachaCoin && (
            <ItemFreeGachaDialog
              onClaimSuccess={async () => {
                await refetchStats();
              }}
              numberOfSocialsLinked={statsData?.socialConnected}
              isGachaCoinClaimable={
                statsData?.gachaCoinEligibility.isGachaCoinClaimable
              }
              isPlayedPayingGames={
                statsData?.gachaCoinEligibility.isPlayedPayingGames
              }
            >
              <FreeGachaDialogButton />
            </ItemFreeGachaDialog>
          )}
          {user && (
            <button
              onClick={() => navigate({ to: "/inventory" })}
              className="dark-blue-elevated-container hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98 z-50 h-9 flex items-center justify-between"
              style={{
                borderWidth: "2px 2px 2px 2px",
              }}
            >
              <img
                src={`https://cdn.navalabs.io/icons/bag_S5thrx.png`}
                alt={"Inventory Icon"}
                className="size-6 mr-1 -mt-1"
              />
              <span
                className="uppercase text-white text-xs font-black"
                style={{
                  textShadow: generateTextShadow(1),
                }}
              >
                {t("shop.inventory")}
              </span>
            </button>
          )}
          <button
            onClick={() => router.navigate({ to: "/gacha/history" })}
            className="dark-blue-elevated-container hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98 z-50 h-9 flex items-center justify-center"
            style={{
              borderWidth: "2px 2px 2px 2px",
            }}
          >
            <span
              className="uppercase text-white text-xs font-black"
              style={{
                textShadow: generateTextShadow(1),
              }}
            >
              Gacha History
            </span>
          </button>{" "}
        </div>

        <FloatingLeftButton
          onClick={() => router.navigate({ to: `/home` })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />

        <div className="flex flex-col items-center justify-center w-full h-full">
          <div className="flex-1 min-h-0 flex flex-col justify-center items-center w-full relative">
            <div
              className={cn(
                "flex-1 min-h-0 w-full transition duration-300",
                isPending && "grayscale brightness-50",
              )}
            >
              <Carousel
                className={cn(
                  "flex h-full w-full items-center",
                  "[&>div]:relative [&>div]:h-full [&>div]:flex [&>div]:flex-col [&>div]:items-center",
                  // enable for debugging
                  // "border border-red-500",
                )}
                opts={{ align: "center", skipSnaps: true, loop: true }}
                setApi={setCarouselApi}
              >
                <CarouselContent className="flex-1 flex items-center select-none">
                  {Array.from({ length: 9 }, (_, i) => {
                    const tier = (i % 3) + 1;
                    const tierKey = tierOrder[i % tierOrder.length];
                    const isCentered = i === activeIndex;
                    return (
                      <CarouselItem
                        key={i}
                        className="px-1"
                        style={{
                          flex: "0 0 50%",
                        }}
                      >
                        <div
                          className={cn(
                            "flex flex-col items-center justify-center relative",
                            isCentered && "scale-200 xs:scale-220",
                            !isCentered && "scale-150 xs:scale-150",
                          )}
                          style={{
                            ...(!isCentered && {
                              transform: "translateY(20px)",
                            }),
                          }}
                        >
                          {isCentered && (
                            // <div
                            //   className="absolute -top-5 text-[#2EC835] font-bold mb-2 uppercase"
                            //   style={{
                            //     fontSize: "3rem",
                            //     textShadow: generateTextShadow(1.5),
                            //     imageRendering: "pixelated",
                            //   }}
                            // >
                            //   TIER {tier}
                            // </div>
                            <img
                              src={GACHA_TITLE[tier - 1]}
                              alt={`Gacha Tier ${tierKey}`}
                              className="absolute top-0 h-10 w-auto text-[#2EC835] font-bold mb-2 uppercase"
                              style={{
                                imageRendering: "pixelated",
                              }}
                            />
                          )}
                          {isCentered && !hasSeenPointer && (
                            <div className="absolute h-[15vh] top-[50%] right-[25%] z-30 pointer-events-none pointer animate-in fade-in">
                              <img
                                className="w-10 block origin-[0%_0%] animate-[click_1s_ease-in-out_infinite_both]"
                                src="https://cdn.navalabs.io/components/gacha/pointer_A42Em2.png"
                                alt="Pointer"
                                style={{
                                  imageRendering: "pixelated",
                                }}
                              />
                            </div>
                          )}
                          <div className="flex items-center justify-center w-full relative">
                            <button
                              type="button"
                              disabled={!isCentered}
                              onClick={() => {
                                if (!isCentered) {
                                  return;
                                }
                                markPointerSeen();
                                setIsMachineDialogOpen(true);
                              }}
                              className="w-full bg-transparent border-0 p-0 cursor-pointer disabled:cursor-default"
                              aria-label={`Open ${tierKey} gacha machine dialog`}
                            >
                              <img
                                src={tierImages[tierKey]}
                                alt={`Gacha Machine ${tierKey}`}
                                data-gacha-machine
                                className="transition-transform duration-300 ease-out will-change-transform w-full"
                                style={{
                                  imageRendering: "pixelated",
                                  ...(!isCentered && {
                                    filter:
                                      "brightness(0.85) sepia(1) hue-rotate(190deg) saturate(400%)",
                                  }),
                                }}
                              />
                            </button>
                          </div>
                        </div>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
              </Carousel>
            </div>

            {isPending && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                {/*<div className="absolute inset-y-0 w-1/2 bg-[#1BD96A] animate-[gacha-loading-bar_1s_linear_infinite]" />*/}
                <img
                  src="/assets/loading-pixel.png"
                  className="w-1/8"
                  style={{
                    imageRendering: "pixelated",
                  }}
                />
              </div>
            )}

            {/* Button below machine */}
            <div className="absolute bottom-4 flex items-center justify-center w-full max-w-md px-4 mt-8">
              <div className="flex flex-col w-full gap-2">
                {/* Increment section (small) */}
                <DefaultButton
                  className="h-10 py-0! dark-blue-container"
                  disabled={currentTier === 3}
                >
                  <div className="flex items-center justify-between gap-2 px-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuantity((prev) => Math.max(1, prev - 1));
                      }}
                      disabled={quantity <= 1 || currentTier === 3}
                      className="w-6 h-6 flex items-center justify-center text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        imageRendering: "pixelated",
                        textShadow: generateTextShadow(1),
                      }}
                    >
                      <img
                        src={
                          "https://cdn.navalabs.io/assets/default-minus_rL2axK.png"
                        }
                        alt={t("entriesCounter.altTexts.decrement")}
                        className="size-5/6"
                        style={{ imageRendering: "pixelated" }}
                      />
                    </button>
                    <span
                      className="text-white font-bold min-w-6 text-center text-lg"
                      style={{
                        imageRendering: "pixelated",
                        textShadow: generateTextShadow(1),
                      }}
                    >
                      {quantity}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuantity((prev) => Math.min(MAX_QUANTITY, prev + 1));
                      }}
                      disabled={quantity >= MAX_QUANTITY || currentTier === 3}
                      className="w-6 h-6 flex items-center justify-center text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        imageRendering: "pixelated",
                        textShadow: generateTextShadow(1),
                      }}
                    >
                      <img
                        src={
                          "https://cdn.navalabs.io/assets/default-plus_6vqK24.png"
                        }
                        alt={t("entriesCounter.altTexts.increment")}
                        className="size-5/6"
                        style={{ imageRendering: "pixelated" }}
                      />
                    </button>
                  </div>
                </DefaultButton>
                {/* Price section (wide) */}
                <DefaultButton
                  className="flex-1 flex items-center justify-center gap-2 min-h-10 uppercase text-sm"
                  disabled={
                    currentTier === 3 ||
                    isPending ||
                    (statsData?.gachaCoin ?? 0) < totalPrice
                  }
                  onClick={() => setIsConfirmSpinOpen(true)}
                >
                  {currentTier === 3 ? (
                    <span
                      style={{
                        textShadow: generateTextShadow(1),
                      }}
                    >
                      {t("inventory.outOfPrizes")}
                    </span>
                  ) : isPending ? (
                    <AnimatedWaiting prefix="LOADING" />
                  ) : (statsData?.gachaCoin ?? 0) < totalPrice ? (
                    <div className="flex items-center space-x-1 -mb-1">
                      <span
                        style={{
                          textShadow: generateTextShadow(1),
                        }}
                      >
                        REQUIRED {totalPrice.toLocaleString()}
                      </span>
                      <img
                        src={
                          "https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png"
                        }
                        alt="Gacha Coin"
                        className="w-5 h-5"
                        style={{
                          imageRendering: "pixelated",
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1 -mb-1">
                      <span
                        style={{
                          textShadow: generateTextShadow(1),
                        }}
                      >
                        SPIN WITH {totalPrice.toLocaleString()}
                      </span>
                      <img
                        src={
                          "https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png"
                        }
                        alt="Gacha Coin"
                        className="w-5 h-5"
                        style={{
                          imageRendering: "pixelated",
                        }}
                      />
                    </div>
                  )}
                </DefaultButton>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Activity mode={isVideoOpen ? "visible" : "hidden"}>
        <div className="fixed inset-0 flex items-center justify-center z-1001! bg-black">
          <div className="w-full max-w-md px-1">
            <video
              // key={videoInstance}
              ref={videoRef}
              className="h-full w-full sm:scale-94 mx-auto object-cover"
              src={GACHA_VIDEO_SRC}
              controls={false}
              playsInline
              muted={true}
              autoPlay
              preload="auto"
              onClick={handleSkipToReveal}
              onEnded={() => {
                if (!isVideoOpen) {
                  return;
                }
                setIsVideoOpen(false);
                restoreMusicIfNeeded();
                if (revealedItems.length > 0) {
                  setIsTransitionOpen(true);
                  setTimeout(() => {
                    setTransitionPhase("ready");
                  }, 1000);
                }
              }}
            />
          </div>
        </div>
      </Activity>

      <Activity mode={isTransitionOpen ? "visible" : "hidden"}>
        <button
          type="button"
          onClick={() => {
            if (!isTransitionReady) {
              return;
            }
            setIsTransitionOpen(false);
            if (revealedItems.length > 0) {
              setIsRevealOpen(true);
            }
          }}
          className="fixed inset-0 z-1002! flex items-center justify-center text-left bg-black"
        >
          <div className="w-full px-1 max-w-md relative flex flex-col items-center gap-4 text-center">
            <img
              src={TRANSITION_OPENED_SRC}
              className="w-full sm:scale-95 object-cover"
            />
            {isTransitionReady && (
              <>
                <div className="absolute h-[15vh] top-[75%] right-[10%] z-30 pointer-events-none pointer animate-in fade-in">
                  <img
                    className="w-20 block origin-[0%_0%] animate-[click_1s_ease-in-out_infinite_both]"
                    src="https://cdn.navalabs.io/components/gacha/pointer_A42Em2.png"
                    alt="Pointer"
                    style={{
                      imageRendering: "pixelated",
                    }}
                  />
                </div>
                <div
                  className="absolute top-5/6 text-xs uppercase tracking-[0.2em] text-white animate-in fade-in duration-1000"
                  style={{
                    animation: "gacha-bob 1.1s ease-in-out 500ms infinite",
                    textShadow: generateTextShadow(1),
                  }}
                >
                  Tap the ball to open
                </div>
              </>
            )}
          </div>
        </button>
      </Activity>

      {activeRevealItem && Object.keys(activeRevealItem).length && (
        <Activity mode={isRevealOpen ? "visible" : "hidden"}>
          <div className="fixed inset-0 z-1002! flex items-center justify-center bg-black/92 px-6 text-left">
            <div
              key={activeRevealItem?.sequenceId ?? ""}
              className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-4 text-center overflow-x-visible"
            >
              <div
                className="text-sm uppercase tracking-[0.3em] text-[#9BE7FF] z-100"
                style={{
                  textShadow: generateTextShadow(0.55),
                  ...(isRevealReady
                    ? {
                        animation: "gacha-float-in 420ms ease-out 120ms both",
                      }
                    : { opacity: 0 }),
                }}
              >
                Gacha Reward
              </div>
              <div
                className="text-3xl font-bold text-white z-100 uppercase"
                style={{
                  ...(isRevealReady
                    ? {
                        animation: "gacha-float-in 420ms ease-out 160ms both",
                      }
                    : { opacity: 0 }),
                }}
              >
                <span style={{ textShadow: generateTextShadow(1.5) }}>
                  {activeRevealItem?.itemName}
                </span>
              </div>
              <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
                <div className="pointer-events-none absolute inset-0">
                  {SPARKLE_BURSTS.map((sparkle, index) => (
                    <div
                      key={index}
                      className={cn(
                        "absolute left-1/2 top-1/2 opacity-0",
                        sparkle.shape === "diamond" ? "rotate-45" : "",
                      )}
                      style={
                        {
                          animation: `gacha-sparkle 720ms ease-out ${1.7 + sparkle.delay}s forwards`,
                          "--x": `${sparkle.x}px`,
                          "--y": `${sparkle.y}px`,
                        } as unknown as CSSProperties
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 480 480"
                        style={
                          {
                            width: `${sparkle.size}px`,
                            height: `${sparkle.size}px`,
                            color: "var(--color-yellow-200)",
                            filter: "drop-shadow(0 0 8px currentColor)",
                          } as CSSProperties
                        }
                      >
                        <path
                          d="M480 240A240 240 0 0 1 240 0 240 240 0 0 1 0 240a240 240 0 0 1 240 240 240 240 0 0 1 240-240Z"
                          fill="currentColor"
                        ></path>
                      </svg>
                    </div>
                  ))}
                  <ApngOnce
                    src={REVEAL_ORB_SRC}
                    fallbackSrc={REVEAL_ORB_FALLBACK_SRC}
                    className="absolute w-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                    style={{ mixBlendMode: "screen" }}
                    onEnded={handleRevealAnimationEnd}
                  />

                  <div
                    className={cn(
                      "absolute inset-0 scale-170 w-full h-full transform pointer-events-none",
                      isRevealReady ? "animate-in zoom-in" : "opacity-0",
                    )}
                    aria-hidden={!isRevealReady}
                  >
                    <svg
                      className={cn(
                        "absolute inset-0 w-full h-full transform",
                        isRevealReady && "animate-[spin_30s_linear_infinite]",
                      )}
                      viewBox="0 0 348 430"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g style={{ mixBlendMode: "color-dodge" }} opacity="0.5">
                        <path
                          d="M208.991 205.149L398.749 119.302L386.222 97.6046L381.267 89.0204L368.739 67.3234L199.516 188.734L320.927 19.511L299.23 6.98326L290.645 2.02834L268.948 -10.4994L183.101 179.259L203.633 -28H178.58H168.67H143.617L164.149 179.259L78.3016 -10.4994L56.6046 2.02834L48.0204 6.98326L26.3234 19.511L147.734 188.734L-21.489 67.3234L-34.0167 89.0204L-38.9717 97.6046L-51.4994 119.302L138.259 205.149L-69 184.617V209.67V219.58V244.633L138.259 224.101L-51.4994 309.948L-38.9717 331.645L-34.0167 340.23L-21.489 361.927L147.734 240.516L26.3234 409.739L48.0204 422.267L56.6046 427.222L78.3016 439.749L164.149 249.991L143.617 457.25H168.67H178.58H203.633L183.101 249.991L268.948 439.749L290.645 427.222L299.23 422.267L320.927 409.739L199.516 240.516L368.739 361.927L381.267 340.23L386.222 331.645L398.749 309.948L208.991 224.101L416.25 244.633V219.58V209.67V184.617L208.991 205.149Z"
                          fill="url(#paint0_radial_2693_1216)"
                          style={{ mixBlendMode: "overlay" }}
                        />
                      </g>
                      <defs>
                        <radialGradient
                          id="paint0_radial_2693_1216"
                          cx="0"
                          cy="0"
                          r="1"
                          gradientUnits="userSpaceOnUse"
                          gradientTransform="translate(173.625 214.625) scale(242.625)"
                        >
                          <stop stop-color="#FFDF20" stop-opacity="0.75" />
                          <stop
                            offset="0.723097"
                            stop-color="white"
                            stop-opacity="0"
                          />
                        </radialGradient>
                      </defs>
                    </svg>
                  </div>
                </div>

                <div
                  className="flex-1 flex items-center justify-center pointer-events-none"
                  style={{
                    animation:
                      "gacha-pop 720ms cubic-bezier(0.16, 1, 0.3, 1) 1.65s both",
                  }}
                >
                  {Object.keys(MODEL_REGISTRY).includes(
                    activeRevealItem?.itemId || "",
                  ) ? (
                    (() => {
                      const ModelComponent =
                        MODEL_REGISTRY[activeRevealItem?.itemId as ModelType];
                      return (
                        <div className="w-full overflow-auto object-cover pointer-events-none">
                          <Activity mode={isRevealReady ? "visible" : "hidden"}>
                            <Canvas style={{ pointerEvents: "none" }}>
                              <OrthographicCamera
                                makeDefault
                                position={[0, 0, 1000]}
                                zoom={3}
                              />
                              <ambientLight
                                position={[0, 0, 10]}
                                intensity={3}
                              />
                              <directionalLight
                                position={[0, THREE.MathUtils.degToRad(40), 0]}
                                intensity={12}
                              />
                              <ModelComponent rotation={0} />
                            </Canvas>
                          </Activity>
                        </div>
                      );
                    })()
                  ) : (
                    <ItemIcon
                      item={activeRevealItem}
                      className={cn(
                        activeRevealItem?.itemName === "Trophy"
                          ? "w-20"
                          : "w-28",
                        "h-auto object-cover pointer-events-none",
                        activeRevealItem?.itemType === "chart-line" &&
                          "size-40 drop-shadow-[0_10px_0_rgba(0,0,0,0.3)]",
                      )}
                      userProfilePicture={statsData?.profilePicture}
                      size={112}
                    />
                  )}
                </div>
              </div>
              {/*<p
              className="max-w-xs text-sm text-white/80"
              style={{
                textShadow: generateTextShadow(1),
                animation: "gacha-float-in 420ms ease-out 220ms both",
              }}
            >
              {activeRevealItem.description}
            </p>*/}
              <div
                className="flex flex-col items-center gap-3 mt-6"
                style={{
                  animation: isRevealReady
                    ? "gacha-float-in 420ms ease-out 240ms both"
                    : "none",
                  opacity: isRevealReady ? 1 : 0,
                }}
              >
                <button
                  onClick={() => {
                    if (!isRevealReady) {
                      return;
                    }
                    if (activeRevealIndex + 1 < revealedItems.length) {
                      void handleRevealAdvance();
                    } else {
                      // Open prize summary dialog
                      setIsRevealOpen(false);
                      setIsPrizeSummaryOpen(true);
                    }
                  }}
                  disabled={!isRevealReady}
                  className="green-elevated-container text-sm sm:text-base uppercase py-2! px-6! mt-2! hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98"
                  style={{
                    animation: isRevealReady
                      ? "gacha-bob 1.1s ease-in-out 500ms infinite"
                      : "none",
                    textShadow: generateTextShadow(1),
                  }}
                >
                  {activeRevealIndex + 1 < revealedItems.length
                    ? "View Next Prize"
                    : "View Prizes"}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isRevealReady) {
                      return;
                    }
                    handleShareToTwitter();
                  }}
                  disabled={!isRevealReady}
                  className="gray-container text-xs sm:text-sm px-2 w-full py-2 flex items-center justify-center hover:cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-98 uppercase"
                  aria-label="Share to Twitter"
                  style={{
                    animation: isRevealReady
                      ? "gacha-float-in 420ms ease-out 240ms both"
                      : "none",
                    textShadow: generateTextShadow(1),
                  }}
                >
                  Share on X
                </button>
              </div>
              <div
                className="text-xs text-white/60"
                style={{
                  animation: isRevealReady
                    ? "gacha-float-in 420ms ease-out 260ms both"
                    : "none",
                  opacity: isRevealReady ? 1 : 0,
                }}
              >
                {activeRevealIndex + 1} / {revealedItems.length}
              </div>
            </div>
          </div>
        </Activity>
      )}

      {/* Confirmation Modal */}
      <Dialog open={isConfirmSpinOpen} onOpenChange={setIsConfirmSpinOpen}>
        <DialogContent
          aria-label="Confirm spin dialog"
          className="relative max-w-[20rem]! p-4"
        >
          <div className="flex flex-col items-center gap-3">
            <span
              className="text-white font-black text-xl text-center uppercase"
              style={{ textShadow: generateTextShadow(1.4) }}
            >
              Confirm Spin
            </span>

            <p
              className="text-white/70 text-xs text-center"
              style={{ textShadow: generateTextShadow(0.8) }}
            >
              Are you sure you want to spend your Gacha Coins? This action
              cannot be undone.
            </p>

            <div className="w-full p-2 flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span
                  className="text-white text-xs uppercase"
                  style={{ textShadow: generateTextShadow(0.8) }}
                >
                  Tier
                </span>
                <span
                  className="text-white font-bold text-sm uppercase"
                  style={{ textShadow: generateTextShadow(1) }}
                >
                  {tierOrder[currentTier - 1]} (Tier {currentTier})
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span
                  className="text-white text-xs uppercase"
                  style={{ textShadow: generateTextShadow(0.8) }}
                >
                  Quantity
                </span>
                <span
                  className="text-white font-bold text-sm"
                  style={{ textShadow: generateTextShadow(1) }}
                >
                  {quantity}x
                </span>
              </div>
              <div className="h-px bg-white/10" />
              <div className="flex items-center justify-between">
                <span
                  className="text-white text-xs uppercase"
                  style={{ textShadow: generateTextShadow(0.8) }}
                >
                  Total Cost
                </span>
                <div className="flex items-center gap-1">
                  <span
                    className="text-white font-black text-sm"
                    style={{ textShadow: generateTextShadow(1) }}
                  >
                    {totalPrice.toLocaleString()}
                  </span>
                  <img
                    src="https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png"
                    alt="Gacha Coin"
                    className="w-4 h-4"
                    style={{ imageRendering: "pixelated" }}
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-2 w-full">
              <DefaultButton
                className="flex-1 min-h-10 uppercase text-sm"
                onClick={() => setIsConfirmSpinOpen(false)}
              >
                <span style={{ textShadow: generateTextShadow(1) }}>
                  Cancel
                </span>
              </DefaultButton>
              <DefaultButton
                className="flex-1 min-h-10 uppercase text-sm"
                variant="green"
                onClick={() => {
                  setIsConfirmSpinOpen(false);
                  handleGachaClaim();
                }}
              >
                <span style={{ textShadow: generateTextShadow(1) }}>Spin!</span>
              </DefaultButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Prize Summary Dialog */}
      <PrizeSummaryDialog
        open={isPrizeSummaryOpen}
        onOpenChange={setIsPrizeSummaryOpen}
        revealedItems={revealedItems}
        userProfilePicture={statsData?.profilePicture}
        onSpinAgain={() => {
          setIsPrizeSummaryOpen(false);
          setRevealedItems([]);
          setActiveRevealIndex(0);
        }}
      />
    </>
  );
}
